import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { swapAPI, walletAPI, settingsAPI } from '../services/api';
import { useNotifications } from './NotificationContext';
import { useAuth } from './AuthContext';
import { DEFAULT_VAT_PERCENTAGE } from '../utils/vat';

const SwapContext = createContext();

export const SwapProvider = ({ children }) => {
  const { showCustomToast } = useNotifications();
  const { user } = useAuth();

  // ── State Management ────────────────────────────────────────────
  const [swaps, setSwaps] = useState([]);
  const [currentSwap, setCurrentSwap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [swapMetadata, setSwapMetadata] = useState({
    totalSwaps: 0,
    pendingSwaps: 0,
    completedSwaps: 0,
    cancelledSwaps: 0,
  });

  // Track rate limit errors to prevent spamming toasts
  const rateLimitErrorRef = useRef(false);

  // Admin-configurable VAT rate, applied to the swap platform fee (not the
  // swap value itself) — same rate used for seller commission VAT.
  const [vatPercentage, setVatPercentage] = useState(DEFAULT_VAT_PERCENTAGE);
  useEffect(() => {
    settingsAPI.getVatRate()
      .then((res) => {
        const pct = res?.data?.vat_percentage ?? res?.vat_percentage;
        if (typeof pct === 'number' && pct > 0) setVatPercentage(pct);
      })
      .catch(() => {});
  }, []);

  // ── Fetch Wallet Balance ────────────────────────────────────────
  const fetchWalletBalance = useCallback(async () => {
    try {
      const response = await walletAPI.getBalance();
      // apiClient returns the parsed body ({ success, message, data }) — not an
      // axios envelope — so the payload is response.data, never response.data.data.
      if (response?.success || response?.data) {
        const balance = response.data?.balance ?? 0;
        setWalletBalance(balance);
        return balance;
      }
    } catch (err) {
      // Silently handle rate limit errors to avoid spamming
      if (err.message?.includes('Rate limit') || err.message?.includes('429')) {
        return null;
      }
      console.error('Failed to fetch wallet balance:', err);
      return null;
    }
  }, []);

  // ── Fetch All Swaps ────────────────────────────────────────────
  const fetchSwaps = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const response = await swapAPI.getSwaps();
      // Handle all known response shapes from the backend
      const raw = response?.data?.data?.swap_offers
        || response?.data?.data
        || response?.data?.swap_offers
        || response?.data?.swap_deals
        || response?.data;
      const swapsData = Array.isArray(raw) ? raw : [];
      setSwaps(swapsData);
      rateLimitErrorRef.current = false;

      const metadata = {
        totalSwaps: swapsData.length,
        pendingSwaps: swapsData.filter(s => s.status?.includes('pending')).length,
        completedSwaps: swapsData.filter(s => s.status === 'completed').length,
        cancelledSwaps: swapsData.filter(s => s.status === 'cancelled').length,
      };
      setSwapMetadata(metadata);
    } catch (err) {
      // Silently handle rate limit errors to avoid spamming toasts
      if (err.message?.includes('Rate limit') || err.message?.includes('429')) {
        if (!rateLimitErrorRef.current) {
          rateLimitErrorRef.current = true;
          console.warn('API rate limit reached. Please try again later.');
        }
        return;
      }

      // Silently handle 404 — user may simply have no swaps yet
      const status = err.response?.status;
      if (status === 404) {
        setSwaps([]);
        return;
      }

      console.error('Failed to fetch swaps:', err);
      setError(err.message || 'Failed to fetch swaps');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Helper: Build device description from form data
  const buildDeviceDescription = (offerData) => {
    const parts = [];
    if (offerData.storage) parts.push(`Storage: ${offerData.storage}`);
    if (offerData.condition_rating) parts.push(`Condition: ${offerData.condition_rating}/10`);
    if (offerData.device_type !== 'laptop') {
      if (offerData.battery_health !== undefined) parts.push(`Battery: ${offerData.battery_health}%`);
      if (offerData.face_id_status) parts.push(`Face ID: ${offerData.face_id_status}`);
    } else {
      if (offerData.ram) parts.push(`RAM: ${offerData.ram}`);
      if (offerData.cpu) parts.push(`CPU: ${offerData.cpu}`);
      if (offerData.graphics) parts.push(`Graphics: ${offerData.graphics}`);
    }
    if (offerData.repair_history) parts.push(`Repairs: ${offerData.repair_history}`);
    return parts.join(' | ');
  };

  // Helper: Map condition rating to description
  const mapConditionRating = (rating) => {
    if (rating <= 3) return 'Poor';
    if (rating <= 5) return 'Fair';
    if (rating <= 7) return 'Good';
    if (rating <= 9) return 'Excellent';
    return 'Perfect';
  };

  // Helper: Extract brand from model string
  const extractBrandFromModel = (model) => {
    if (!model) return 'Unknown';
    const modelUpper = model.toUpperCase();
    if (modelUpper.includes('IPHONE')) return 'Apple';
    if (modelUpper.includes('SAMSUNG')) return 'Samsung';
    if (modelUpper.includes('PIXEL')) return 'Google';
    if (modelUpper.includes('ONEPLUS')) return 'OnePlus';
    if (modelUpper.includes('MOTOROLA')) return 'Motorola';
    if (modelUpper.includes('INTEL')) return 'Intel';
    if (modelUpper.includes('AMD')) return 'AMD';
    if (modelUpper.includes('APPLE')) return 'Apple';
    if (modelUpper.includes('M1') || modelUpper.includes('M2')) return 'Apple';
    return model.split(' ')[0] || 'Device';
  };

  // ── Submit Swap Offer (Step 1) ──────────────────────────────────
  const submitSwapOffer = useCallback(async (offerData) => {
    setLoading(true);
    setError(null);
    try {
      // Validate required fields
      if (!offerData.listing_product_id) {
        throw new Error('Product to swap for is required');
      }
      if (!offerData.device_type) {
        throw new Error('Device type is required');
      }
      if (!offerData.model) {
        throw new Error('Device model is required');
      }
      if (!offerData.condition_rating || offerData.condition_rating < 1 || offerData.condition_rating > 10) {
        throw new Error('Condition rating must be between 1 and 10');
      }
      if (!offerData.images || offerData.images.length < 5) {
        throw new Error('Minimum 5 images required');
      }
      // IMEI 1 is required for the device being offered; IMEI 2 is optional
      // (dual-SIM). Neither is ever shown back to the initiator or to the
      // seller — admin-only, for warehouse inspection.
      if (!/^\d{15}$/.test((offerData.imei1 || '').trim())) {
        throw new Error('IMEI 1 is required and must be exactly 15 digits');
      }
      if (offerData.imei2 && !/^\d{15}$/.test(offerData.imei2.trim())) {
        throw new Error('IMEI 2 must be exactly 15 digits if provided');
      }

      // Build the swap offer request per API spec
      const swapRequest = {
        listing_product_id: offerData.listing_product_id,
        device_type: offerData.device_type,
        model: offerData.model,
        storage: offerData.storage || 'N/A',
        condition_rating: parseInt(offerData.condition_rating),
        battery_health: offerData.battery_health ? parseInt(offerData.battery_health) : undefined,
        face_id_status: offerData.face_id_status || undefined,
        repair_history: offerData.repair_history || undefined,
        images: offerData.images || [],
        imei1: offerData.imei1.trim(),
        imei2: offerData.imei2?.trim() || undefined,
      };

      const response = await swapAPI.submitOffer(swapRequest);
      const swap = response?.data?.data || response?.data;
      if (swap) {
        showCustomToast({
          type: 'success',
          title: 'Swap Offer Submitted',
          message: `Your offer ${swap.swap_number || ''} has been sent to the seller`.trim(),
          duration: 4000,
        });
        await fetchSwaps();
        return swap;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to submit swap offer';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Submission Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Respond to Offer with Top-Up Price (Step 3B - Seller) ───────
  const respondToOffer = useCallback(async (swapId, topupAmount) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');
      if (typeof topupAmount !== 'number' || topupAmount < 0) {
        throw new Error('Top-up amount must be 0 or greater');
      }

      const response = await swapAPI.respondToOffer(swapId, topupAmount);
      if (response?.success) {
        const feeData = response.data;
        showCustomToast({
          type: 'success',
          title: 'Price Offered',
          message: `Top-up of ₦${topupAmount.toLocaleString()} sent to buyer`,
          duration: 4000,
        });
        await fetchSwaps();
        return feeData;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to respond to offer';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Response Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Decline Offer (Step 3A - Seller) ────────────────────────────
  const declineOffer = useCallback(async (swapId) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');

      const response = await swapAPI.declineOffer(swapId);
      if (response?.success) {
        showCustomToast({
          type: 'info',
          title: 'Offer Declined',
          message: 'The swap offer has been declined',
        });
        await fetchSwaps();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to decline offer';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Decline Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Accept Price (Step 5B - Initiator) ──────────────────────────
  const acceptPrice = useCallback(async (swapId) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');

      const response = await swapAPI.acceptPrice(swapId);
      if (response?.success) {
        const checkoutData = response.data;
        showCustomToast({
          type: 'success',
          title: 'Price Accepted',
          message: 'Proceed to payment to complete the swap',
          duration: 4000,
        });
        setCurrentSwap(checkoutData);
        await fetchSwaps();
        return checkoutData;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to accept price';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Acceptance Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Decline Price (Step 5A - Initiator) ─────────────────────────
  const declinePrice = useCallback(async (swapId) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');

      const response = await swapAPI.declinePrice(swapId);
      if (response?.success) {
        showCustomToast({
          type: 'info',
          title: 'Price Declined',
          message: 'You can submit a new offer or try another product',
        });
        await fetchSwaps();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to decline price';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Decline Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Process Payment (Step 6 - Initiator) ────────────────────────
  const processSwapPayment = useCallback(
    async (swapId, paymentMethod = 'wallet', logisticsFee = 2500) => {
      setLoading(true);
      setError(null);
      try {
        if (!swapId) throw new Error('Swap ID is required');

        // Check wallet balance if paying via wallet
        if (paymentMethod === 'wallet') {
          const currentBalance = await fetchWalletBalance();
          const swap = swaps.find(s => s.id === swapId || s._id === swapId);
          if (!swap) throw new Error('Swap not found');

          // Mirror the backend's charge exactly: total_payable (top-up +
          // platform fee + VAT, computed server-side) plus the logistics fee.
          const totalAmount = (swap.total_payable
            ?? ((swap.seller_topup_amount || 0) + (swap.platform_fee || 0) + (swap.vat_amount || 0)))
            + (logisticsFee || 0);

          if (currentBalance < totalAmount) {
            throw new Error(
              `Insufficient wallet balance. Need ₦${totalAmount.toLocaleString()}, but have ₦${currentBalance.toLocaleString()}`
            );
          }
        }

        // Process payment
        const response = await swapAPI.processPayment(swapId, {
          payment_method: paymentMethod,
          logistics_fee: logisticsFee,
        });

        if (response?.success) {
          const paymentData = response.data;
          showCustomToast({
            type: 'success',
            title: 'Payment Processed',
            message: `Escrow created: ${paymentData.escrow_number}. Your swap is now in progress.`,
            duration: 5000,
          });
          setCurrentSwap(paymentData);
          await fetchSwaps();
          await fetchWalletBalance();
          return paymentData;
        }
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message || 'Payment processing failed';
        setError(errorMsg);
        showCustomToast({
          type: 'error',
          title: 'Payment Failed',
          message: errorMsg,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [swaps, fetchSwaps, fetchWalletBalance, showCustomToast]
  );

  // ── Confirm Shipment (Step 7 - Initiator) ───────────────────────
  const confirmShipment = useCallback(async (swapId, trackingData) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');
      const response = await swapAPI.confirmShipment(swapId, trackingData);
      if (response?.success) {
        showCustomToast({
          type: 'success',
          title: 'Shipment Confirmed',
          message: 'Tracking info submitted. Awaiting delivery confirmation.',
          duration: 4000,
        });
        await fetchSwaps();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to confirm shipment';
      setError(errorMsg);
      showCustomToast({ type: 'error', title: 'Shipment Failed', message: errorMsg });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Confirm Delivery (Step 8) ────────────────────────────────────
  const confirmDelivery = useCallback(async (swapId, isInitiator) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');
      const response = await swapAPI.confirmDelivery(swapId, isInitiator);
      if (response?.success) {
        showCustomToast({
          type: 'success',
          title: 'Delivery Confirmed',
          message: 'Delivery confirmed. Awaiting swap completion.',
          duration: 4000,
        });
        await fetchSwaps();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to confirm delivery';
      setError(errorMsg);
      showCustomToast({ type: 'error', title: 'Delivery Confirmation Failed', message: errorMsg });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Complete Swap (Step 9 - Final) ───────────────────────────────
  const completeSwap = useCallback(async (swapId) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');
      const response = await swapAPI.completeSwapDeal(swapId);
      if (response?.success) {
        showCustomToast({
          type: 'success',
          title: 'Swap Completed!',
          message: 'The swap deal has been completed successfully.',
          duration: 5000,
        });
        await fetchSwaps();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to complete swap';
      setError(errorMsg);
      showCustomToast({ type: 'error', title: 'Completion Failed', message: errorMsg });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Cancel Swap ─────────────────────────────────────────────────
  const cancelSwap = useCallback(async (swapId) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');

      const response = await swapAPI.cancelSwap(swapId);
      if (response?.success) {
        showCustomToast({
          type: 'info',
          title: 'Swap Cancelled',
          message: 'The swap has been cancelled',
        });
        await fetchSwaps();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to cancel swap';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Cancellation Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSwaps, showCustomToast]);

  // ── Get Single Swap Details ─────────────────────────────────────
  const getSwapDetails = useCallback(async (swapId) => {
    setLoading(true);
    setError(null);
    try {
      if (!swapId) throw new Error('Swap ID is required');
      const response = await swapAPI.getSwap(swapId);
      if (response?.success || response?.data) {
        const swapData = response.data;
        setCurrentSwap(swapData);
        return swapData;
      }
    } catch (err) {
      console.error('Failed to fetch swap details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Get Swaps by Status ─────────────────────────────────────────
  const getSwapsByStatus = useCallback((status) => {
    return swaps.filter(swap => swap.status === status);
  }, [swaps]);

  // ── Get User's Initiated Swaps (as buyer) ───────────────────────
  const getInitiatedSwaps = useCallback(() => {
    const userId = user?.id || user?._id;
    if (!userId) return [];
    return swaps.filter(swap =>
      String(swap.initiator_id) === String(userId) ||
      String(swap.buyer_id) === String(userId)
    );
  }, [swaps, user]);

  // ── Get User's Received Swaps (as seller) ───────────────────────
  const getReceivedSwaps = useCallback(() => {
    const userId = user?.id || user?._id;
    if (!userId) return [];
    return swaps.filter(swap =>
      String(swap.listing_seller_id) === String(userId) ||
      String(swap.recipient_id) === String(userId) ||
      String(swap.seller_id) === String(userId)
    );
  }, [swaps, user]);

  // ── Calculate Swap Fees with Tier-Based Platform Fee ──────────────
  const calculateSwapFees = useCallback((topupAmount) => {
    if (typeof topupAmount !== 'number' || topupAmount < 0) {
      throw new Error('Top-up amount must be a non-negative number');
    }

    let platformFeePercentage;

    // Tier-based platform fee calculation
    if (topupAmount >= 500000) {
      platformFeePercentage = 0.05; // 5%
    } else if (topupAmount >= 400000) {
      platformFeePercentage = 0.06; // 6%
    } else if (topupAmount >= 300000) {
      platformFeePercentage = 0.09; // 9%
    } else if (topupAmount >= 200000) {
      platformFeePercentage = 0.10; // 10%
    } else if (topupAmount >= 100000) {
      platformFeePercentage = 0.40; // 40%
    } else if (topupAmount >= 10000) {
      platformFeePercentage = 0.50; // 50%
    } else {
      platformFeePercentage = 0; // No fee for amounts below ₦10K
    }

    const platformFee = topupAmount * platformFeePercentage;
    const vat = platformFee * (vatPercentage / 100);
    const total = topupAmount + platformFee + vat;

    return {
      topupAmount,
      platformFee: Math.round(platformFee),
      vat: Math.round(vat),
      total: Math.round(total),
      platformFeePercentage: Math.round(platformFeePercentage * 100),
      vatPercentage,
    };
  }, [vatPercentage]);

  // Legacy alias for backward compatibility
  const calculateFeeBreakdown = useCallback((topupAmount) => {
    return calculateSwapFees(topupAmount);
  }, [calculateSwapFees]);

  // ── Initialize on Mount ─────────────────────────────────────────
  useEffect(() => {
    if (user) {
      // Small delay to prevent immediate multiple calls
      const timer = setTimeout(() => {
        fetchSwaps();
        fetchWalletBalance();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [user]);

  // ── Context Value ───────────────────────────────────────────────
  const value = {
    // State
    swaps,
    currentSwap,
    loading,
    error,
    walletBalance,
    swapMetadata,

    // Actions - Swap Lifecycle
    submitSwapOffer,
    respondToOffer,
    declineOffer,
    acceptPrice,
    declinePrice,
    processSwapPayment,
    cancelSwap,
    confirmShipment,
    confirmDelivery,
    completeSwap,

    // Queries
    getSwapDetails,
    getSwapsByStatus,
    getInitiatedSwaps,
    getReceivedSwaps,
    fetchSwaps,
    fetchWalletBalance,

    // Utilities
    calculateSwapFees,
    calculateFeeBreakdown,
  };

  return <SwapContext.Provider value={value}>{children}</SwapContext.Provider>;
};

// ── Custom Hook ──────────────────────────────────────────────────
export const useSwap = () => {
  const context = useContext(SwapContext);
  if (!context) {
    throw new Error('useSwap must be used within a SwapProvider');
  }
  return context;
};

export default SwapContext;
